import { useState, useEffect, useCallback } from 'react';
import {
  CurrencyDollarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  GlobeAltIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import {
  TaxConfiguration,
  CreateTaxConfigRequest,
  TaxConfigurationHistory,
  TaxChangeNotification,
  TaxScope,
  TAX_SCOPES,
  SCOPE_COLORS,
  RDC_PROVINCES,
  HOTEL_CATEGORIES,
  TaxConfigSearchParams,
} from '../../types/taxConfig';
import {
  fetchTaxConfigurations,
  createTaxConfiguration,
  updateTaxConfiguration,
  deactivateTaxConfiguration,
  fetchConfigHistory,
  notifyAffectedHotels,
} from '../../services/taxConfigService';

const emptyForm: CreateTaxConfigRequest = {
  name: '',
  fpt_percentage: 10,
  tva_percentage: 16,
  effective_start_date: new Date().toISOString().split('T')[0],
  effective_end_date: '',
  scope: 'NATIONAL',
  scope_value: '',
};

export default function TaxConfigurationPage() {
  const { hasPermission } = usePermissions();
  const [configs, setConfigs] = useState<TaxConfiguration[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<TaxConfiguration | null>(null);
  const [formData, setFormData] = useState<CreateTaxConfigRequest>({ ...emptyForm });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Detail panel
  const [historyLog, setHistoryLog] = useState<TaxConfigurationHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [lastNotification, setLastNotification] = useState<TaxChangeNotification | null>(null);

  // --- Data loading ---
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: TaxConfigSearchParams = { limit: 100 };
      if (searchTerm) params.search = searchTerm;
      if (scopeFilter) params.scope = scopeFilter;
      if (statusFilter === 'active') params.active_only = true;
      if (statusFilter === 'inactive') params.active_only = false;
      const result = await fetchTaxConfigurations(params);
      setConfigs(Array.isArray(result.data) ? result.data : []);
      setTotal(result.total ?? 0);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des configurations');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, scopeFilter, statusFilter]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Derived stats
  const activeCount = configs.filter(c => c.is_active).length;
  const nationalCount = configs.filter(c => c.scope === 'NATIONAL').length;
  const provinceCount = configs.filter(c => c.scope === 'PROVINCE').length;
  const categoryCount = configs.filter(c => c.scope === 'CATEGORY').length;

  // --- Validation ---
  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Le nom est requis';
    if (formData.fpt_percentage < 0 || formData.fpt_percentage > 100) return 'Le taux FPT doit être entre 0 et 100';
    if (formData.tva_percentage < 0 || formData.tva_percentage > 100) return 'Le taux TVA doit être entre 0 et 100';
    if (!formData.effective_start_date) return 'La date de début est requise';
    if (formData.effective_end_date && formData.effective_end_date <= formData.effective_start_date) {
      return 'La date de fin doit être après la date de début';
    }
    if ((formData.scope === 'PROVINCE' || formData.scope === 'CATEGORY') && !formData.scope_value) {
      return 'La valeur de portée est requise pour les portées Province et Catégorie';
    }
    return null;
  };

  // --- Handlers ---
  const handleCreateConfig = async () => {
    const validationError = validateForm();
    if (validationError) { setFormError(validationError); return; }
    setSaving(true);
    setFormError(null);
    try {
      const submitData = { ...formData };
      if (!submitData.effective_end_date) delete submitData.effective_end_date;
      if (submitData.scope === 'NATIONAL') delete submitData.scope_value;
      await createTaxConfiguration(submitData);
      setShowCreateModal(false);
      setFormData({ ...emptyForm });
      setSuccessMsg('Configuration fiscale créée avec succès');
      await loadConfigs();
    } catch (err: any) {
      setFormError(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleEditConfig = async () => {
    if (!selectedConfig) return;
    const validationError = validateForm();
    if (validationError) { setFormError(validationError); return; }
    setSaving(true);
    setFormError(null);
    try {
      const submitData = { ...formData };
      if (!submitData.effective_end_date) delete submitData.effective_end_date;
      if (submitData.scope === 'NATIONAL') delete submitData.scope_value;
      await updateTaxConfiguration(selectedConfig.id, submitData);
      setShowEditModal(false);
      setSelectedConfig(null);
      setFormData({ ...emptyForm });
      setSuccessMsg('Configuration fiscale mise à jour avec succès');
      await loadConfigs();
    } catch (err: any) {
      setFormError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (config: TaxConfiguration) => {
    if (!window.confirm(`Désactiver la configuration "${config.name}" ?`)) return;
    setActionError(null);
    try {
      await deactivateTaxConfiguration(config.id);
      setSuccessMsg('Configuration désactivée avec succès');
      await loadConfigs();
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la désactivation');
    }
  };

  const handleNotifyHotels = async (config: TaxConfiguration) => {
    setNotifying(true);
    try {
      const result = await notifyAffectedHotels(config.id);
      setLastNotification(result);
      setSuccessMsg(`${result.hotels_notified} hôtel(s) notifié(s) avec succès`);
    } catch (err: any) {
      setActionError(err.message || 'Erreur lors de la notification');
    } finally {
      setNotifying(false);
    }
  };

  const handleViewDetail = async (config: TaxConfiguration) => {
    setSelectedConfig(config);
    setShowDetailPanel(true);
    setHistoryLoading(true);
    setHistoryLog([]);
    setLastNotification(null);
    try {
      const result = await fetchConfigHistory(config.id);
      setHistoryLog(Array.isArray(result.data) ? result.data : []);
    } catch {
      setHistoryLog([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openEditModal = (config: TaxConfiguration) => {
    setSelectedConfig(config);
    setFormData({
      name: config.name,
      fpt_percentage: config.fpt_percentage,
      tva_percentage: config.tva_percentage,
      effective_start_date: config.effective_start_date?.split('T')[0] || '',
      effective_end_date: config.effective_end_date?.split('T')[0] || '',
      scope: config.scope,
      scope_value: config.scope_value || '',
    });
    setFormError(null);
    setShowEditModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setScopeFilter('');
    setStatusFilter('active');
  };

  const hasActiveFilters = searchTerm || scopeFilter || statusFilter !== 'active';

  // --- Helpers ---
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'NATIONAL': return <GlobeAltIcon className="w-4 h-4" />;
      case 'PROVINCE': return <MapPinIcon className="w-4 h-4" />;
      case 'CATEGORY': return <BuildingOfficeIcon className="w-4 h-4" />;
      default: return null;
    }
  };

  const getScopeBadge = (scope: string, scopeValue?: string | null) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SCOPE_COLORS[scope] || 'bg-gray-100 text-gray-800'}`}>
      {getScopeIcon(scope)}
      <span className="ml-1">
        {TAX_SCOPES.find(s => s.value === scope)?.label || scope}
        {scopeValue ? ` — ${scopeValue}` : ''}
      </span>
    </span>
  );

  const getStatusBadge = (isActive: boolean) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
    }`}>
      {isActive ? <CheckCircleIcon className="w-3 h-3 mr-1" /> : <XCircleIcon className="w-3 h-3 mr-1" />}
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const getHistoryActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      CREATED: 'bg-green-100 text-green-700',
      UPDATED: 'bg-blue-100 text-blue-700',
      DEACTIVATED: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors[action] || 'bg-gray-100 text-gray-700'}`}>
        {action}
      </span>
    );
  };

  // --- Scope value selector in form ---
  const renderScopeValueField = () => {
    if (formData.scope === 'PROVINCE') {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700">Province *</label>
          <select value={formData.scope_value || ''}
            onChange={e => setFormData({ ...formData, scope_value: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">Sélectionner une province</option>
            {RDC_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      );
    }
    if (formData.scope === 'CATEGORY') {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700">Catégorie d'hôtel *</label>
          <select value={formData.scope_value || ''}
            onChange={e => setFormData({ ...formData, scope_value: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">Sélectionner une catégorie</option>
            {HOTEL_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      );
    }
    return null;
  };

  // --- Form modal renderer ---
  const renderFormModal = (title: string, onSubmit: () => void, onClose: () => void, isCreate: boolean) => (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{formError}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom de la configuration *</label>
              <input type="text" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Configuration Kinshasa 2025"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">FPT (%) *</label>
                <input type="number" min="0" max="100" step="0.01" value={formData.fpt_percentage}
                  onChange={e => setFormData({ ...formData, fpt_percentage: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Fonds de Promotion Touristique</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">TVA (%) *</label>
                <input type="number" min="0" max="100" step="0.01" value={formData.tva_percentage}
                  onChange={e => setFormData({ ...formData, tva_percentage: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Taxe sur la Valeur Ajoutée</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Portée *</label>
              <select value={formData.scope}
                onChange={e => setFormData({ ...formData, scope: e.target.value as TaxScope, scope_value: '' })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                {TAX_SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {renderScopeValueField()}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date de début *</label>
                <input type="date" value={formData.effective_start_date}
                  onChange={e => setFormData({ ...formData, effective_start_date: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date de fin</label>
                <input type="date" value={formData.effective_end_date || ''}
                  onChange={e => setFormData({ ...formData, effective_end_date: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Laisser vide pour durée indéterminée</p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Annuler
            </button>
            <button onClick={onSubmit}
              disabled={saving || !formData.name.trim() || !formData.effective_start_date}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Enregistrement...' : isCreate ? 'Créer' : 'Mettre à jour'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Loading skeleton ---
  if (loading && configs.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>)}
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 rounded mb-3"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configuration des Taxes</h1>
              <p className="text-gray-600 mt-1">Gérer les taux FPT et TVA par portée</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={loadConfigs} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" title="Rafraîchir">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {hasPermission('TAX_EDIT') && (
              <button onClick={() => { setFormData({ ...emptyForm }); setFormError(null); setShowCreateModal(true); }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                <PlusIcon className="w-4 h-4 mr-2" />
                Nouvelle Configuration
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success / Error banners */}
      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
          <p className="text-sm text-green-700">{successMsg}</p>
        </div>
      )}
      {(error || actionError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error || actionError}</p>
          <button onClick={() => { setError(null); setActionError(null); }} className="text-red-400 hover:text-red-600">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Configurations</p>
              <p className="text-2xl font-bold text-gray-900">{total || configs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <GlobeAltIcon className="h-8 w-8 text-indigo-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nationales</p>
              <p className="text-2xl font-bold text-gray-900">{nationalCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <MapPinIcon className="h-8 w-8 text-teal-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Provinciales</p>
              <p className="text-2xl font-bold text-gray-900">{provinceCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-amber-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Par Catégorie</p>
              <p className="text-2xl font-bold text-gray-900">{categoryCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Rechercher par nom..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Toutes les portées</option>
              {TAX_SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600">{configs.length} résultat(s)</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  Effacer filtres
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Configurations table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Configuration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FPT / TVA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Portée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates d'effet</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configs.map(config => (
                <tr key={config.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetail(config)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{config.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Créé le {formatDate(config.created_at)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <span className="font-semibold text-indigo-700">{config.fpt_percentage}%</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="font-semibold text-teal-700">{config.tva_percentage}%</span>
                    </div>
                    <div className="text-xs text-gray-500">FPT / TVA</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getScopeBadge(config.scope, config.scope_value)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(config.effective_start_date)}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      → {config.effective_end_date ? formatDate(config.effective_end_date) : 'Indéterminée'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(config.is_active)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={e => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      {hasPermission('TAX_EDIT') && (
                        <button onClick={() => openEditModal(config)} className="text-indigo-600 hover:text-indigo-900" title="Modifier">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('TAX_EDIT') && config.is_active && (
                        <button onClick={() => handleDeactivate(config)} className="text-orange-600 hover:text-orange-900" title="Désactiver">
                          <NoSymbolIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('TAX_EDIT') && (
                        <button onClick={() => handleNotifyHotels(config)} disabled={notifying}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50" title="Notifier les hôtels">
                          <BellAlertIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {configs.length === 0 && !loading && (
          <div className="text-center py-12">
            <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucune configuration fiscale trouvée</p>
            {hasPermission('TAX_EDIT') && (
              <button onClick={() => { setFormData({ ...emptyForm }); setFormError(null); setShowCreateModal(true); }}
                className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                <PlusIcon className="w-4 h-4 mr-2" />Créer une configuration
              </button>
            )}
          </div>
        )}
      </div>

      {/* Detail Slide-over Panel */}
      {showDetailPanel && selectedConfig && (
        <div className="fixed inset-0 z-50 overflow-hidden" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-50" onClick={() => { setShowDetailPanel(false); setSelectedConfig(null); }}></div>
          <div className="absolute inset-y-0 right-0 max-w-lg w-full bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{selectedConfig.name}</h2>
                <button onClick={() => { setShowDetailPanel(false); setSelectedConfig(null); }} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Config info */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-indigo-600 font-medium">FPT</p>
                    <p className="text-2xl font-bold text-indigo-800">{selectedConfig.fpt_percentage}%</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-teal-600 font-medium">TVA</p>
                    <p className="text-2xl font-bold text-teal-800">{selectedConfig.tva_percentage}%</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getScopeBadge(selectedConfig.scope, selectedConfig.scope_value)}
                  {getStatusBadge(selectedConfig.is_active)}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Période:</span>{' '}
                  {formatDate(selectedConfig.effective_start_date)} → {selectedConfig.effective_end_date ? formatDate(selectedConfig.effective_end_date) : 'Indéterminée'}
                </div>
                <div className="text-xs text-gray-400">
                  Créé le {formatDateTime(selectedConfig.created_at)}
                </div>
              </div>

              {/* Affected hotels info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-800 flex items-center mb-2">
                  <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                  Hôtels affectés
                </h4>
                <p className="text-sm text-blue-700">
                  {selectedConfig.scope === 'NATIONAL' && 'Tous les hôtels actifs sont affectés par cette configuration nationale.'}
                  {selectedConfig.scope === 'PROVINCE' && `Hôtels de la province de ${selectedConfig.scope_value || '—'}.`}
                  {selectedConfig.scope === 'CATEGORY' && `Hôtels de catégorie ${selectedConfig.scope_value || '—'} étoile(s).`}
                </p>
                {hasPermission('TAX_EDIT') && (
                  <button onClick={() => handleNotifyHotels(selectedConfig)} disabled={notifying}
                    className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50">
                    <BellAlertIcon className="w-4 h-4 mr-1" />
                    {notifying ? 'Envoi...' : 'Notifier les hôtels'}
                  </button>
                )}
                {lastNotification && (
                  <p className="mt-2 text-xs text-blue-600">
                    {lastNotification.hotels_notified} hôtel(s) notifié(s) le {formatDateTime(lastNotification.notified_at)}
                  </p>
                )}
              </div>

              {/* Quick actions */}
              <div className="flex space-x-2 mb-6">
                {hasPermission('TAX_EDIT') && (
                  <button onClick={() => { setShowDetailPanel(false); openEditModal(selectedConfig); }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-md hover:bg-indigo-200">
                    <PencilIcon className="w-4 h-4 mr-1" /> Modifier
                  </button>
                )}
                {hasPermission('TAX_EDIT') && selectedConfig.is_active && (
                  <button onClick={() => { setShowDetailPanel(false); handleDeactivate(selectedConfig); }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-md hover:bg-orange-200">
                    <NoSymbolIcon className="w-4 h-4 mr-1" /> Désactiver
                  </button>
                )}
              </div>

              {/* History */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-blue-600" />
                Historique des modifications
              </h3>
              {historyLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded"></div>)}
                </div>
              ) : historyLog.length > 0 ? (
                <div className="space-y-3">
                  {historyLog.map((entry, idx) => (
                    <div key={entry.id || idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        {getHistoryActionBadge(entry.action)}
                        <span className="text-xs text-gray-400">{formatDateTime(entry.changed_at)}</span>
                      </div>
                      {entry.action === 'UPDATED' && (
                        <div className="mt-2 text-xs text-gray-600 space-y-1">
                          {entry.old_fpt_percentage != null && entry.new_fpt_percentage != null && (
                            <div>FPT: {entry.old_fpt_percentage}% → {entry.new_fpt_percentage}%</div>
                          )}
                          {entry.old_tva_percentage != null && entry.new_tva_percentage != null && (
                            <div>TVA: {entry.old_tva_percentage}% → {entry.new_tva_percentage}%</div>
                          )}
                        </div>
                      )}
                      {entry.action === 'CREATED' && (
                        <div className="mt-2 text-xs text-gray-600">
                          FPT: {entry.new_fpt_percentage}% | TVA: {entry.new_tva_percentage}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucun historique disponible</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && renderFormModal(
        'Nouvelle Configuration Fiscale',
        handleCreateConfig,
        () => { setShowCreateModal(false); setFormError(null); },
        true
      )}

      {/* Edit Modal */}
      {showEditModal && renderFormModal(
        'Modifier la Configuration Fiscale',
        handleEditConfig,
        () => { setShowEditModal(false); setSelectedConfig(null); setFormError(null); },
        false
      )}
    </div>
  );
}
